/**
 * Profile editor (`/me/profile`).
 *
 * Two editable fields and one read-only identity: the nickname is personal
 * data, the interface language is both a local preference and an account
 * setting, and the email is the login identity the beta does not let anyone
 * change.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { LIMITS } from '@shared/constants'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, isLocale, type Locale } from '@shared/locales'

import { PageHeader } from '../../components/layout/page-header'
import { ErrorAlert } from '../../components/layout/states'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { Field, FieldGroup } from '../../components/ui/field'
import { Input, Label } from '../../components/ui/input'
import { useCurrentUser, useUpdateProfile } from '../../hooks/use-auth'
import { changeLocale, LOCALE_LABELS } from '../../lib/i18n'
import { mutationErrorText } from '../../lib/mutation-error'

export function ProfilePage() {
  const { t, i18n } = useTranslation()
  const { user } = useCurrentUser()
  const updateProfile = useUpdateProfile()

  const [nickname, setNickname] = useState(user?.nickname ?? '')
  const [locale, setLocale] = useState<Locale>(
    user && isLocale(user.locale) ? user.locale : DEFAULT_LOCALE,
  )
  const [localError, setLocalError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  // The account is re-read on every successful save, so the form follows the
  // server value instead of drifting from it.
  useEffect(() => {
    if (!user) {
      return
    }
    setNickname(user.nickname ?? '')
    setLocale(isLocale(user.locale) ? user.locale : DEFAULT_LOCALE)
  }, [user])

  function submit() {
    const value = nickname.trim()
    if (value.length < LIMITS.nicknameMinLength || value.length > LIMITS.nicknameMaxLength) {
      setLocalError(t('profile.nicknameError'))
      setSaved(false)
      return
    }
    setLocalError(null)

    updateProfile.mutate(
      { nickname: value, locale },
      {
        onSuccess: async () => {
          // The language switch is applied locally as well: the account only
          // records the preference, the running i18next instance has to follow.
          if (locale !== i18n.language) {
            await changeLocale(locale)
          }
          setSaved(true)
        },
      },
    )
  }

  const errorText = mutationErrorText(updateProfile.error, t)

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('nav.me'), to: '/me' },
          { label: t('nav.profile') },
        ]}
        title={t('profile.title')}
      />

      <Card>
        <CardContent className="pt-4 sm:pt-5">
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault()
              submit()
            }}
          >
            <FieldGroup>
              <Field label={t('profile.emailLabel')} htmlFor="profile-email" hint={t('profile.emailHint')}>
                <Input id="profile-email" value={user?.email ?? ''} readOnly aria-readonly />
              </Field>

              <Field
                label={t('profile.nicknameLabel')}
                htmlFor="profile-nickname"
                required
                error={localError}
                hint={`${t('profile.nicknamePlaceholder')} · ${t('me.profileHint')}`}
              >
                <Input
                  id="profile-nickname"
                  name="nickname"
                  autoComplete="nickname"
                  maxLength={LIMITS.nicknameMaxLength}
                  value={nickname}
                  aria-invalid={Boolean(localError)}
                  onChange={(event) => setNickname(event.target.value)}
                />
              </Field>

              <div className="flex flex-col gap-2">
                <Label htmlFor="profile-locale">{t('profile.localeLabel')}</Label>
                <select
                  id="profile-locale"
                  name="locale"
                  className="border-input bg-background h-9 w-full rounded-md border px-3 text-sm shadow-sm"
                  value={locale}
                  onChange={(event) => setLocale(event.target.value as Locale)}
                >
                  {SUPPORTED_LOCALES.map((value) => (
                    <option key={value} value={value}>
                      {LOCALE_LABELS[value]}
                    </option>
                  ))}
                </select>
              </div>
            </FieldGroup>

            {errorText ? (
              <ErrorAlert message={errorText} />
            ) : null}

            {saved ? (
              <Alert variant="success">
                <AlertDescription>{t('profile.saved')}</AlertDescription>
              </Alert>
            ) : null}

            <Button type="submit" disabled={updateProfile.isPending}>
              {t('common.save')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
